/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {Device, GenericObject, JobConflictResolution} from '@ion/core/src/types/entities';
import CopyButton from '@ion/dls/src/components/buttons/CopyButton';
import Divider from '@ion/dls/src/components/divider/Divider';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import InfoTooltip from '@ion/dls/src/components/tooltip/InfoTooltip';
import {CopyLinkIcon} from '@ion/dls/src/icons/CopyLinkIcon';
import {NotificationUrgentIcon} from '@ion/dls/src/icons/NotificationUrgentIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {resolveValue} from '@ion/dls/src/utils/ComponentUtils';
import JobActionSelectionMenu from './JobActionSelectionMenu';
import JobConflictDevicesGrid from './JobConflictDevicesGrid';
import {areAllDevicesInConflicts, getUniqueDeviceCount} from './JobConflictUtils';
import useJobIdCopyToClipboard from './useJobIdCopyToClipboard';

export type JobConflictResolutionChooserProps = {
  selectedTasks: {
    tasks: GenericObject[];
  }
  hasError?: boolean;
  activeStep?: number;
  conflicts: {
    id: string;
    type: string;
    devices: {
      creator: string;
      age: string;
      jobName: string;
      jobId: number;
      task: string;
      deviceIdentifier: string;
    }[];
  }[][];
  currentJobName: string;
  selectedDevices: Device[];
  selectedOptions: Record<number, JobConflictResolution>;
  setSelectedOptions: Function;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    width   : '100%',
    padding : '4px 0px 16px'
  },
  redText : {
    color : dlsColor('caRed')
  },
  header : {
    display      : 'flex',
    alignItems   : 'center',
    gap          : '8px',
    marginBottom : '16px',
    color        : dlsColor('grey2'),
    fontWeight   : 500
  },
  notificationIcon : {
    color : dlsColor('caRed')
  },
  warningText : {
    ...dlsTypography('body2'),
    color      : dlsColor('black'),
    fontSize   : '16px',
    lineHeight : '24px'
  },
  conflictText : {
    ...dlsTypography('body2'),
    color      : ({hasError}: {hasError: boolean}) => dlsColor(hasError ? 'caRed' : 'grey2'),
    fontSize   : '14px',
    lineHeight : '20px',
    margin     : '16px 0px'
  },
  table : {
    width           : '100%',
    maxHeight       : '100%',
    borderCollapse  : 'collapse',
    backgroundColor : dlsColor('white')
  },
  stepperWrapper : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  stepperContainer : {
    minWidth            : '50%',
    marginBottom        : 12,
    '&, .MuiPaper-root' : {
      backgroundColor : 'transparent !important',
      boxShadow       : 'none'
    }
  },
  stepIcon : {
    color                     : dlsColor('caBlue'),
    '&.MuiStepIcon-completed' : {
      color : dlsColor('caBlue')
    },
    '&.MuiStepIcon-active' : {
      color : dlsColor('caBlue')
    },
    '&.MuiStepIcon-root:not(.MuiStepIcon-active)' : {
      color : dlsColor('grey4')
    }
  },
  stepLabel : {
    '&, MuiStepLabel-label' : {
      fontFamily : 'Roboto',
      fontSize   : '14px',
      lineHeight : '20px',
      textAlign  : 'left',
      color      : dlsColor('grey2'),
      fontWeight : '400 !important'
    }
  },
  columnHeader : {
    ...dlsAlt('typography', 'cta2')
  },
  arrow : {
    cursor     : 'pointer',
    paddingTop : '4px',
    transition : 'transform 300ms ease'
  },
  th : {
    textAlign   : 'left',
    padding     : '8px',
    paddingLeft : '16px',
    border      : `1px solid ${dlsColor('grey5')}`
  },
  td : {
    padding     : '8px',
    paddingLeft : '16px',
    border      : `1px solid ${dlsColor('grey5')}`,
    transition  : 'all 300ms ease',
    '&:hover'   : {
      backgroundColor : dlsColor('paleBlue')
    }
  },
  deviceOptionTitle : {
    ...dlsAlt('typography', 'label2'),
    color          : dlsColor('grey1'),
    fontWeight     : 700,
    letterSpacing  : '0px',
    fontSize       : '14px',
    lineHeight     : '20px',
    textDecoration : 'underline',
    cursor         : 'pointer'
  },
  optionTitle : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    letterSpacing : '0px',
    fontSize      : '14px',
    lineHeight    : '20px',
    display       : 'flex',
    alignItems    : 'center'
  },
  optionDetails : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey3'),
    letterSpacing : '0px'
  },
  status : {
    ...dlsAlt('typography', 'cta2'),
    color           : dlsColor('grey3'),
    backgroundColor : dlsColor('grey5'),
    letterSpacing   : '1px',
    borderRadius    : '2px',
    padding         : '2px 4px',
    fontSize        : '10px',
    lineHeight      : '16px',
    display         : 'inline-block',
    marginTop       : '4px'
  },
  columnHeaderContainer : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '12px'
  },
  radioContainer : {
    display    : 'flex',
    alignItems : 'flex-start',
    gap        : '8px',
    minHeight  : '80px'
  },
  radioButton : {
    marginRight : '8px'
  },
  nestedTable : {
    width           : '100%',
    borderCollapse  : 'collapse',
    backgroundColor : dlsColor('grey8'),
    display         : 'flex',
    alignItems      : 'center'
  },
  nestedTd : {
    padding : '8px 16px'
  },
  tableWrapper : {
    minHeight : '500px',
    overflow  : 'visible',
    position  : 'relative'
  },
  thead : {
    position        : 'sticky',
    top             : -1,
    zIndex          : 100,
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`
  },
  copyLink : {
    border          : 'none',
    color           : dlsColor('aaBlue'),
    cursor          : 'pointer',
    alignItems      : 'center',
    display         : 'inline-flex',
    backgroundColor : dlsColor('white')
  }
}));

const JobConflictResolutionChooser = ({
  selectedTasks,
  hasError,
  conflicts,
  activeStep,
  currentJobName,
  selectedDevices,
  selectedOptions,
  setSelectedOptions
}: JobConflictResolutionChooserProps) => {
  const classes                         = useStyles({hasError});
  const copyValuesToClipboard           = useJobIdCopyToClipboard();
  const count                           = getUniqueDeviceCount(conflicts);
  const isHideRemoveOption              = areAllDevicesInConflicts(selectedDevices, conflicts);
  const {CANCEL, REMOVE, IGNORE}        = JobConflictResolution;
  const [expandedRows, setExpandedRows] = useState<{[key: string]: boolean}>({});

  const conflictingTaskTypes = conflicts?.flat()?.map(conflict => `${conflict.type}`)?.filter(Boolean);

  const types = selectedTasks?.tasks
    ?.filter(task => conflictingTaskTypes.includes(`${task?.task?.deviceType} ${task?.task?.fileType}`))
    ?.map(task => `${task?.task?.deviceType} ${task?.task?.fileType}`);

  const toggleDeviceList = (rowId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId] : !prev[rowId]
    }));
  };

  const handleRadioChange = useCallback((rowId, taskIndex, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [`${rowId}-${taskIndex}`] : value
    }));
  }, [setSelectedOptions]);

  const handleHeaderRadioChange = useCallback(value => {
    setSelectedOptions(prev => {
      const updatedOptions = {...prev};
      const conflictSet = conflicts?.[activeStep];
      conflictSet?.forEach?.((conflict) => {
        conflict?.devices?.forEach?.(device => {
          updatedOptions[`${conflict?.id}-${device?.task}`] = value;
        });
      });
      return updatedOptions;
    });
  }, [activeStep, conflicts]);

  const deselectAllInColumn = useCallback((columnValue) => {
    setSelectedOptions((prev) => {
      const newSelectedOptions = {...prev};
      const conflictSet        = conflicts?.[activeStep];
      conflictSet.forEach((conflict) => {
        conflict.devices.forEach((device) => {
          const key = `${conflict.id}-${device.task}`;
          if (newSelectedOptions[key] === columnValue) {
            delete newSelectedOptions[key];
          }
        });
      });

      return newSelectedOptions;
    });
  }, [activeStep, conflicts, setSelectedOptions]);

  const formatDeviceFileTypeText = useCallback((types: string[]) => {
    if (types?.length === 1) {
      return (
        <strong>{types?.[0]}</strong>
      );
    }
    const formattedElements = [];
    types.forEach((type, index) => {
      formattedElements.push(<strong key={type}>{type}</strong>);
      if (index < types?.length - 2) {
        formattedElements.push(', ');
      } else if (index === types?.length - 2) {
        formattedElements.push(' and ');
      }
    });
    return formattedElements;
  }, []);

  const onRadioChange = useCallback((rowId, task, type: JobConflictResolution) => () => {
    handleRadioChange(rowId, task, type);
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <NotificationUrgentIcon size={IconSize.medium} color={dlsColor('caRed')} />
        <div className={classes.warningText}>
          <strong>
            {translate(`jobcreate:conflict.properties.${count > 1 ? 'devices' : 'device'}`, {
              count : count
            })}
          </strong>
          {translate('jobcreate:conflict.warning.inProgress')}
          {formatDeviceFileTypeText(types)}
        </div>
      </div>
      <Divider />
      <div className={classes.conflictText}>
        <ConditionalRender condition={hasError}>
          {translate('jobcreate:conflict.warning.selection')}
        </ConditionalRender>
        <ConditionalRender condition={!hasError}>
          {`${translate('jobcreate:conflict.text')}:`}
        </ConditionalRender>
      </div>
      <ConditionalRender condition={conflicts?.length > 1}>
        <div className={classes.stepperWrapper}>
          <Stepper activeStep={activeStep} className={classes.stepperContainer}>
            {conflicts?.map((_, index) => (
              <Step key={index}>
                <StepLabel StepIconProps={{
                  classes : {root: classes.stepIcon}
                }}
                classes={{label: classes.stepLabel}}>
                  {conflicts?.[index]?.[0]?.type}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
      </ConditionalRender>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            <tr>
              <th className={classes.th}>
                <div className={classes.columnHeaderContainer}>
                  <div className={classes.arrow}>
                    <JobActionSelectionMenu count={conflicts?.[activeStep]?.length}
                      handleSelect={function() { handleHeaderRadioChange(CANCEL); }}
                      handleDeselect={function() { deselectAllInColumn(CANCEL); }}
                      disable={!Object?.values?.(selectedOptions)?.includes(CANCEL)}/>
                  </div>
                  <span className={classes.columnHeader}>
                    {translate('jobcreate:conflict.resolution.cancel.label')}
                  </span>
                  <InfoTooltip message={translate('jobcreate:conflict.resolution.cancel.tooltip')} />
                </div>
              </th>
              <ConditionalRender condition={!isHideRemoveOption}>
                <th className={classes.th}>
                  <div className={classes.columnHeaderContainer}>
                    <div className={classes.arrow}>
                      <JobActionSelectionMenu count={conflicts?.[activeStep]?.length}
                        handleSelect={function() { handleHeaderRadioChange(REMOVE); }}
                        handleDeselect={function() { deselectAllInColumn(REMOVE); }}
                        disable={!Object?.values?.(selectedOptions)?.includes(REMOVE)}/>
                    </div>
                    <span className={classes.columnHeader}>
                      {translate('jobcreate:conflict.resolution.remove.label')}
                    </span>
                    <InfoTooltip message={translate('jobcreate:conflict.resolution.remove.tooltip')} />
                  </div>
                </th>
              </ConditionalRender>
              <th className={classes.th}>
                <div className={classes.columnHeaderContainer}>
                  <div className={classes.arrow}>
                    <JobActionSelectionMenu count={conflicts?.[activeStep]?.length}
                      handleSelect={function() { handleHeaderRadioChange(IGNORE); }}
                      handleDeselect={function() { deselectAllInColumn(IGNORE); }}
                      disable={!Object?.values?.(selectedOptions)?.includes(IGNORE)}/>
                  </div>
                  <span className={classes.columnHeader}>
                    {translate('jobcreate:conflict.resolution.ignore.label')}
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {conflicts?.[activeStep]?.map((row, i) => {
              const rowId   = row?.id;
              const devices = row?.devices;
              const count   = devices?.length;
              const device  = devices?.[0];
              const task    = device?.task;
              const jobId   = device?.jobId;
              const jobName = device?.jobName;
              const age     = device?.age;
              const creator = device?.creator;
              const name    = `${rowId}-${task}`;
              return (
                <>
                  <tr key={`${rowId}-${i}`}>
                    <td className={classes.td}>
                      <div className={classes.radioContainer}>
                        <RadioButton label={''}
                          name={`row-${name}`}
                          checked={selectedOptions[name] === CANCEL}
                          onChange={onRadioChange(rowId, task, CANCEL)}/>
                        <div>
                          <div className={classes.optionTitle}>
                            <span className={classes.deviceOptionTitle}
                              onClick={function() { toggleDeviceList(name); }}>
                              {translate(`jobcreate:conflict.properties.${count > 1 ? 'devices' : 'device'}`, {
                                count
                              })}
                            </span>
                          &nbsp;
                            {translate('jobcreate:conflict.properties.jobDetails', {
                              jobId : jobId,
                              task  : Number(task) + 1
                            })}
                            <CopyButton size={IconSize.smallMedium}
                              onCopy={function() { copyValuesToClipboard(jobId); }} />
                          </div>
                          <ConditionalRender condition={jobName}>
                            <div className={classes.optionDetails}>
                              {translate('jobcreate:conflict.properties.jobName')}: {jobName}
                            </div>
                          </ConditionalRender>
                          <ConditionalRender condition={age}>
                            <div className={classes.optionDetails}>
                              {translate('jobcreate:conflict.properties.created')}: {age}
                            </div>
                          </ConditionalRender>
                          <div className={classes.optionDetails}>
                            {translate('jobcreate:conflict.properties.creator')}: {resolveValue(creator)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <ConditionalRender condition={!isHideRemoveOption}>
                      <td className={classes.td}>
                        <div className={classes.radioContainer}>
                          <RadioButton label={''}
                            name={`row-${name}`}
                            checked={selectedOptions[name] === REMOVE}
                            onChange={onRadioChange(rowId, task, REMOVE)}/>
                          <div>
                            <span className={classes.deviceOptionTitle}
                              onClick={function() { toggleDeviceList(name); }}>
                              {translate(`jobcreate:conflict.properties.${count > 1 ? 'devices' : 'device'}`, {
                                count
                              })}
                            </span>
                            <div className={classes.optionDetails}>
                              {translate('jobcreate:conflict.properties.jobName')}: {currentJobName}
                            </div>
                          </div>
                        </div>
                      </td>
                    </ConditionalRender>
                    <td className={classes.td}>
                      <div className={classes.radioContainer}>
                        <RadioButton label={''}
                          name={`row-${name}`}
                          checked={selectedOptions[name] === IGNORE}
                          onChange={onRadioChange(rowId, task, IGNORE)}/>
                      </div>
                    </td>
                  </tr>
                  {expandedRows[name] && (
                    <tr>
                      <td colSpan={4}>
                        <table className={classes.nestedTable}>
                          <JobConflictDevicesGrid devices={devices}
                            onClose={function() { toggleDeviceList(name); }}/>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobConflictResolutionChooser;
